import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components/macro';
import { Layout } from 'components';
import { InboxNav, SearchBar, StatusNav } from 'components/workdesk';
import { useDispatch, useSelector } from 'react-redux';
import {
  latestWithSearchTicketsCountSelector,
  selectedStatusTicketGroupWithTicketsSelector,
  wdTicketsNofromWsLengthSelector,
  selectedStatusTicketGroupSelector,
} from 'store/selectors/workdesk-tickets.selector';
import { workdeskTicketsActions } from 'store/actions/workdesk-tickets.actions';
import { workdeskFilterSelector } from 'store/selectors';
import { hadCalledApiSelector } from 'store/selectors/workdesk-filter.selector';
import { createSelector } from '@reduxjs/toolkit';
import { ticketLabelColorMap } from 'store/models/workdesk-tickets/WorkdeskTickets';
import { LinearProgress, Tooltip } from '@material-ui/core';
import { alertsActions, workdeskFilterActions } from 'store/actions';
import TicketGroupTable from 'components/workdesk/ticket-group-table/TicketGroupTable';
import { authSelector } from 'store/selectors/user.selector';

import { WorkdeskFilter } from 'store/models/WorkdeskFilter';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import {
  hasInboxesSelector,
  inboxesStoreStateSelector,
} from 'store/selectors/inboxes.selector';
import moment from 'utils/app-moment';
import {
  connectErrorSelector,
  isWsConnectedSelector,
  useWebSocketSelector,
} from 'store/selectors/websocket.selector';

import ExportData from 'components/workdesk/ExportData';
import {
  pingWsAfterSelector,
  wsCurStatusInboxesLatestReceivedTimeSelector,
  wsCurStatusInboxesLatestReceivedTimeWithDataSelector,
} from 'store/selectors/workdesk-filter.selector';
// import { webSocketActions } from 'store/actions/websocket.actions';

export const isPageLoadingSelector = createSelector(
  inboxesStoreStateSelector,
  authSelector,
  (inboxesS, authentication) => {
    return inboxesS.isLoading || authentication.isLoadingMeInfo;
  },
);

const WorkdeskPage = () => {
  const dispatch = useDispatch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableDispatch = useCallback(dispatch, []);
  const isLoading = useSelector(isPageLoadingSelector);

  const workdeskFilter = useSelector(workdeskFilterSelector);
  const {
    searchValues,
    // selectedTicketType,
    // statusesStartAt,
    // sortBy,
  } = workdeskFilter;

  const isWsConnected = useSelector(isWsConnectedSelector);
  const wsLatestReceivedTime = useSelector(
    wsCurStatusInboxesLatestReceivedTimeSelector,
  );
  const wsLatestReceivedTimeWithData = useSelector(
    wsCurStatusInboxesLatestReceivedTimeWithDataSelector,
  );

  useUpdateEffect(() => {
    if (workdeskFilter && !isLoading) {
      console.debug(
        `workdeskFilter is updated so saveToLocalStorage`,
        workdeskFilter,
      );
      WorkdeskFilter.saveToLocalStorage(workdeskFilter);
    }
  }, [workdeskFilter, isLoading]);

  useEffect(() => {
    stableDispatch(
      workdeskFilterActions.setHadWorkdeskFilterInLsOnInit(
        WorkdeskFilter.hasValueInSessionStorage(),
      ),
    );
  }, [stableDispatch]);

  // date for sort
  const selectedStatusTicketGroup = useSelector(
    selectedStatusTicketGroupWithTicketsSelector,
  );
  // for sort dependency
  const selectedStatusTicketGroupIds = useSelector(
    selectedStatusTicketGroupSelector,
  );

  const isLoadingTickets = useMemo(
    () => selectedStatusTicketGroupIds.isLoading,
    [selectedStatusTicketGroupIds],
  );

  const latestWithSearchTicketsCount = useSelector(
    latestWithSearchTicketsCountSelector,
  );

  // for checking number with the result from the api (when search)
  const wdTicketsNofromWsLength = useSelector(
    wdTicketsNofromWsLengthSelector,
  );

  const hadCalledApi = useSelector(hadCalledApiSelector);

  // for checking number with the result from the api (when search)
  const wcTicketsIncludesWsLength = useMemo(() => {
    return selectedStatusTicketGroup.tickets.length;
  }, [selectedStatusTicketGroup]);

  const groupIsLoading = selectedStatusTicketGroup?.isLoading;
  const groupTicketsRef = useRef([]);
  useEffect(() => {
    groupTicketsRef.current = selectedStatusTicketGroup.tickets;
  }, [selectedStatusTicketGroup]);
  const searchValuesRef = useRef({});
  useEffect(() => {
    searchValuesRef.current = searchValues;
  }, [searchValues]);

  useEffect(() => {
    if (
      hadCalledApi &&
      latestWithSearchTicketsCount !== undefined &&
      !groupIsLoading
    ) {
      const invalid =
        wdTicketsNofromWsLength !== latestWithSearchTicketsCount &&
        wcTicketsIncludesWsLength !== latestWithSearchTicketsCount;
      if (invalid) {
        console.warn(
          `check invalid search tickets length: show = (not from ws ${wdTicketsNofromWsLength}) (all ${wcTicketsIncludesWsLength}), from api = ${latestWithSearchTicketsCount}`,
          'SearchValues: ',
          searchValuesRef.current,
          groupTicketsRef.current,
        );
      } else {
        console.debug(`check invalid search tickets length: pass`);
      }
    }
  }, [
    latestWithSearchTicketsCount,
    wdTicketsNofromWsLength,
    wcTicketsIncludesWsLength,
    hadCalledApi,
    // just added
    groupIsLoading,
  ]);

  const wsIsConnected = useSelector(isWsConnectedSelector);
  const useWebSocket = useSelector(useWebSocketSelector);

  const connectError = useSelector(connectErrorSelector);
  const pingWsAfter = useSelector(pingWsAfterSelector);
  const pingWsAfterRef = useRef(pingWsAfter);
  useEffect(() => {
    pingWsAfterRef.current = pingWsAfter;
  }, [pingWsAfter]);
  const hasInboxes = useSelector(hasInboxesSelector);

  const doInitFetch = useMemo(
    () =>
      !isLoading &&
      hasInboxes &&
      ((useWebSocket && wsIsConnected) || // wait for connect if useWebSocket
        !useWebSocket), // do not wait for connect if not use
    [isLoading, useWebSocket, wsIsConnected, hasInboxes],
  );

  /**
   * load data onInit (when inboxes on the nav is loaded)
   * if any selected inboxes then load workdesk tickets
   * because if load onload ([]) but no selected inbox yet won't get result
   * IMPORTANT: there is a bug about the load too many times
   * careful not to change the dependency
   */
  useEffect(() => {
    // isLoading waits for meInfo to loaded, and we need that to be done before load the tickets because we need the userId to be there to create AssigneeIdToInfo so can do the first assign
    if (doInitFetch) {
      console.debug(
        '****** call loadWorkdeskTickets [1] after first load inboxes,',
        'change selected inboxes,',
        `ws connected. (pingWsAfter = ${pingWsAfterRef.current})`,
      );
      // load with pinging getting update from websocket
      stableDispatch(
        workdeskTicketsActions.loadWorkdeskTickets(
          pingWsAfterRef.current,
        ),
      );
    }
  }, [doInitFetch, stableDispatch]);

  const renderTicketGroup = () => {
    const ticketGroup = {
      ...selectedStatusTicketGroup,
      isLoading: isLoadingTickets,
    };
    if (!ticketGroup) return null;
    return <TicketGroupTable ticketGroup={ticketGroup} />;
  };
  const bottomLabels = useMemo(() => {
    const base = [
      {
        color: ticketLabelColorMap.justChanged,
        label: 'Recently modified (within the session)',
      },
      {
        color: ticketLabelColorMap.justOpened,
        label: 'Recently opened on the Main Page',
      },
    ];
    if (useWebSocket) {
      base.push({
        color: ticketLabelColorMap.fromWs,
        label: 'Realtime updated (every 30s)',
      });
    }
    return base;
  }, [useWebSocket]);

  const shouldShowExportButton = useMemo(() => {
    return (
      selectedStatusTicketGroup &&
      selectedStatusTicketGroup.tickets.length > 0
    );
  }, [selectedStatusTicketGroup]);

  const formatTimeForWs = (
    time: string,
    withData: boolean,
  ): string => {
    if (!time) return '';
    const timeString = moment(time).format('HH:mm:ss');
    if (withData) {
      return `Last data received ${timeString}`;
    }
    return `(Last received ${timeString})`;
  };

  const renderWsLatestReceivedTime = () => {
    return (
      <SLastReceived>
        {formatTimeForWs(wsLatestReceivedTime, false)}
      </SLastReceived>
    );
  };
  const renderWsTime = () => {
    if (!wsLatestReceivedTime) return null;
    return wsLatestReceivedTimeWithData ? (
      <Tooltip
        title={formatTimeForWs(wsLatestReceivedTimeWithData, true)}
      >
        {renderWsLatestReceivedTime()}
      </Tooltip>
    ) : (
      renderWsLatestReceivedTime()
    );
  };
  useEffect(() => {
    const autoUnassign = localStorage.getItem('autoUnassign');
    console.log(
      'reading autoUnassign from localStorage',
      autoUnassign,
    );
    if (autoUnassign) {
      stableDispatch(
        alertsActions.error(
          `Auto unassign from the ticket ${autoUnassign}`,
          true, // keep to alert on the screen in case getting kicked out from being away from screen
        ),
        localStorage.removeItem('autoUnassign'),
      );
    }
  }, [stableDispatch]);

  return (
    <Layout pageName="Workdesk">
      <SBody>
        <StatusNav />
        <SContent>
          {isLoading && <LinearProgress />}
          <SSearchBarWrapper>
            <SearchBar />
            {shouldShowExportButton && <ExportData />}
          </SSearchBarWrapper>
          <SContentGroupsWrapper>
            {renderTicketGroup()}
          </SContentGroupsWrapper>
          <SLabelsWrapper>
            {useWebSocket && (
              <SWebSocketStatusWrapper>
                <SWebSocketStatus>
                  <SWsCircle
                    className={
                      isWsConnected ? 'connected' : 'disconnected'
                    }
                  />
                  web socket is{' '}
                  {isWsConnected ? 'connected' : 'disconnected'}
                  {connectError ? ` (${connectError})` : ''}
                  {renderWsTime()}
                </SWebSocketStatus>
              </SWebSocketStatusWrapper>
            )}
            <SLabelsGroup>
              {bottomLabels.map((item) => (
                <SEachLabel key={item.label}>
                  <SColor color={item.color} />
                  <SText>{item.label}</SText>
                </SEachLabel>
              ))}
            </SLabelsGroup>
          </SLabelsWrapper>
        </SContent>
        <SInboxNav>
          <InboxNav />
        </SInboxNav>
      </SBody>
    </Layout>
  );
};

export default WorkdeskPage;

const SInboxNav = styled.div`
  position: absolute;
  right: 0;
  height: 100%;
`;

const SBody = styled.div`
  display: grid;
  grid-template-columns: 230px auto 81px;
  height: 100%;
  overflow: hidden;
`;
const SContent = styled.div`
  position: relative;
  overflow-y: hidden; // used to be auto when allow scroll out here
`;

const SContentGroupsWrapper = styled.div`
  margin: 1rem 1rem 0.5rem 1rem;
`;
const SSearchBarWrapper = styled.div`
  margin: 1.2rem 1rem 0.5rem 1rem;
  display: grid;
  grid-template-columns: 55% auto;
`;

const SEachLabel = styled.div`
  display: flex;
  align-items: center;
`;
const SText = styled.div``;
const SColor = styled.div`
  background: ${(props: { color: string }) => props.color};
  border-radius: 5px;
  width: 12px;
  height: 12px;
  margin-right: 0.5rem;
  border: solid 1px ${(props) => props.theme.palette.grey[700]};
`;
const SWebSocketStatusWrapper = styled.div`
  display: flex;
  margin-left: 1rem;
`;
const SLabelsGroup = styled.div`
  display: flex;
  column-gap: 0.5rem;
  justify-content: flex-end;
  margin-right: 1rem;
  flex: 1;
`;
const SWebSocketStatus = styled.div`
  /* display: flex;
  align-items: center; */
`;
const SLabelsWrapper = styled.div`
  font-size: 11px;
  display: grid;
  grid-template-columns: 1fr auto;
  color: ${(props) => props.theme.palette.text.secondary};
`;
const SWsCircle = styled.div`
  margin: 0 0.5rem;
  display: inline-flex;
  border-radius: 5px;
  width: 10px;
  height: 10px;
  &.connected {
    background: ${(props) => props.theme.palette.success.main};
  }
  &.disconnected {
    background: ${(props) => props.theme.palette.error.main};
  }
`;
const SLastReceived = styled.div`
  margin-left: 0.5rem;
  font-size: 10px;
  opacity: 80%;
  display: inline;
`;
